import React, { useState } from 'react'
import { Coins } from 'lucide-react'
import { clsx } from 'clsx'
import { getX1TokenIdentity, WRAPPED_NATIVE_MINT } from '@/utils/knownX1Tokens'

/** A stale "SOL"/"Wrapped SOL" label leaked from the community token list. */
function isStaleSolLabel(v?: string | null): boolean {
  if (!v) return false
  return /^w?sol$/i.test(v.trim()) || /wrapped\s+sol/i.test(v.trim())
}

export interface TokenCellProps {
  /** Token mint address (used for the short fallback + key). */
  mint?: string | null
  /** Display name, e.g. "X1 Native Token". */
  name?: string | null
  /** Ticker symbol, e.g. "XNT". */
  symbol?: string | null
  /** Logo URL; on load error we fall back to a letter/coin badge. */
  logoUrl?: string | null
  /** Avatar/logo size. */
  size?: 'sm' | 'md'
  /** Hide the mint-short line under the symbol. */
  hideMint?: boolean
  className?: string
}

function shortMint(mint?: string | null): string {
  if (!mint) return ''
  if (mint === 'native') return 'XNT'
  if (mint.length <= 12) return mint
  return `${mint.slice(0, 4)}…${mint.slice(-4)}`
}

/**
 * TokenCell — a token logo + name/symbol/mint-short identity cell. The logo
 * gracefully degrades: image → first letter of symbol/name → generic coin icon.
 */
export const TokenCell: React.FC<TokenCellProps> = ({
  mint,
  name,
  symbol,
  logoUrl,
  size = 'md',
  hideMint = false,
  className,
}) => {
  const [imgFailed, setImgFailed] = useState(false)

  // Overlay canonical X1 identity for known native/wrapped/XNT mints. This gives
  // the native + wrapped-native mints their real x1.webp logo (instead of the
  // generic Coins fallback) and corrects any stale "SOL"/"Wrapped SOL" symbol or
  // name that leaked from the community token list — WITHOUT touching callers.
  const x1 = getX1TokenIdentity(mint)
  // For known X1 mints the canonical x1.webp mark takes PRECEDENCE over any
  // caller/server-supplied logoUrl (which may be a stale community-list asset,
  // e.g. an external wSOL logo). X1 is a Solana fork, so its representation of
  // native/wrapped/bridged assets renders with the X1 mark. Unknown mints keep
  // the caller's logoUrl.
  const effLogoUrl = x1?.logo ?? (logoUrl && logoUrl.trim() ? logoUrl : logoUrl)
  let effSymbol = symbol
  let effName = name
  if (x1) {
    if (!effSymbol || (mint === WRAPPED_NATIVE_MINT && isStaleSolLabel(effSymbol))) {
      effSymbol = x1.symbol
    }
    if (!effName || isStaleSolLabel(effName)) {
      effName = x1.name
    }
  }

  const avatar = size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-9 w-9 text-sm'
  const letter = (effSymbol || effName || mint || '?').trim().charAt(0).toUpperCase()
  const showImg = !!effLogoUrl && !imgFailed

  const primary = effSymbol || effName || shortMint(mint) || 'Unknown'
  const secondary = effSymbol && effName && effName !== effSymbol ? effName : !hideMint ? shortMint(mint) : ''

  return (
    <div className={clsx('flex items-center gap-2.5 min-w-0', className)}>
      <div
        className={clsx(
          'flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center font-semibold',
          'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-600',
          avatar
        )}
      >
        {showImg ? (
          <img
            src={effLogoUrl as string}
            alt={primary}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : letter && letter !== '?' ? (
          <span aria-hidden="true">{letter}</span>
        ) : (
          <Coins className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
        )}
      </div>

      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{primary}</p>
        {secondary && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">{secondary}</p>
        )}
      </div>
    </div>
  )
}

export default TokenCell
